import is from '@sindresorhus/is';
import { z } from 'zod';

const commentAuthorSchema = z.object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
});

export type CommentAuthor = z.infer<typeof commentAuthorSchema>;

export const commentTargetDescriptionSectionSchema = z.object({
    kind: z.literal('description-section'),
    sectionId: z.string(),
});

export const commentTargetTitleSchema = z.object({
    kind: z.literal('title'),
});

export const commentTargetSkillSchema = z.object({
    kind: z.literal('skill'),
    skillId: z.string(),
});

export const commentTargetWorkflowStepSchema = z.object({
    kind: z.literal('workflow-step'),
    workflowStepId: z.string(),
});

/**
 * Work-task comments (`/work-tasks/{taskId}/comments`) address the task body directly: one
 * description, and each workflow step split into its title and its description by position.
 * Job-profile item comments keep their own `description-section` / `skill` vocabulary above.
 */
export const commentTargetDescriptionSchema = z.object({
    kind: z.literal('description'),
});

export const commentTargetWorkflowStepTitleSchema = z.object({
    kind: z.literal('workflow-step-title'),
    stepIndex: z.number().int().nonnegative(),
});

export const commentTargetWorkflowStepDescriptionSchema = z.object({
    kind: z.literal('workflow-step-description'),
    stepIndex: z.number().int().nonnegative(),
});

export const commentTargetSchema = z.discriminatedUnion('kind', [
    commentTargetDescriptionSectionSchema,
    commentTargetTitleSchema,
    commentTargetSkillSchema,
    commentTargetWorkflowStepSchema,
    commentTargetDescriptionSchema,
    commentTargetWorkflowStepTitleSchema,
    commentTargetWorkflowStepDescriptionSchema,
]);

export type CommentTarget = z.infer<typeof commentTargetSchema>;

export const commentAnchorSchema = z.object({
    quoteSelector: z.object({
        exact: z.string().refine((value) => value.trim().length > 0, 'quoteSelector.exact must not be blank'),
        prefix: z.string().nullish(),
        suffix: z.string().nullish(),
    }),
    positionSelector: z
        .object({
            start: z.number().int().nonnegative(),
            end: z.number().int().nonnegative(),
        })
        .refine(({ start, end }) => end > start, {
            message: 'positionSelector.end must be greater than positionSelector.start',
            path: ['end'],
        }),
    status: z.enum(['anchored', 'unresolved', 'ambiguous']),
});

export type CommentAnchor = z.infer<typeof commentAnchorSchema>;

export const createCommentAnchorSchema = commentAnchorSchema.omit({ status: true });

export type CreateCommentAnchor = z.infer<typeof createCommentAnchorSchema>;

export const commentSubjectVersionSchema = z.union([z.number().int().nonnegative(), z.string().trim().min(1)]);

export type CommentSubjectVersion = z.infer<typeof commentSubjectVersionSchema>;

export const commentResolutionSchema = z.object({
    version: commentSubjectVersionSchema,
    resolvedAt: z.iso.datetime(),
    resolvedBy: commentAuthorSchema.nullish(),
});

export type CommentResolution = z.infer<typeof commentResolutionSchema>;

export const commentSchema = z.object({
    id: z.uuid(),
    parentId: z.uuid().nullish(),
    version: commentSubjectVersionSchema,
    sourceLanguage: z.string(),
    target: commentTargetSchema,
    content: z.string(),
    author: commentAuthorSchema,
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime().nullish(),
    updatedBy: commentAuthorSchema.nullish(),
    anchor: commentAnchorSchema.nullish(),
    resolution: commentResolutionSchema.nullish(),
});

export type Comment = z.infer<typeof commentSchema>;

export const commentsSchema = commentSchema.array();

export type Comments = z.infer<typeof commentsSchema>;

const ANCHORED_COMMENT_TARGET_KINDS = new Set<CommentTarget['kind']>([
    'description-section',
    'description',
    'workflow-step-title',
    'workflow-step-description',
]);

export const createCommentRequestSchema = z
    .object({
        id: z.uuid(),
        version: commentSubjectVersionSchema,
        sourceLanguage: z.string(),
        target: commentTargetSchema,
        content: z.string(),
        anchor: createCommentAnchorSchema.nullish(),
    })
    .superRefine((value, ctx) => {
        // Both comment apis anchor every target that quotes stored text, and reject an anchor on
        // the ones that do not: a job-profile item anchors its description sections, a work task
        // anchors its description and each workflow step half.
        if (ANCHORED_COMMENT_TARGET_KINDS.has(value.target.kind)) {
            if (is.nullOrUndefined(value.anchor)) {
                ctx.addIssue({
                    code: 'custom',
                    message: `anchor is required for ${value.target.kind} targets`,
                    path: ['anchor'],
                });
            }
            return;
        }
        if (!is.nullOrUndefined(value.anchor)) {
            ctx.addIssue({
                code: 'custom',
                message: `anchor must be absent for ${value.target.kind} targets`,
                path: ['anchor'],
            });
        }
    });

export type CreateCommentRequest = z.infer<typeof createCommentRequestSchema>;

export const createReplyRequestSchema = z.object({
    id: z.uuid(),
    content: z.string().min(1),
});

export type CreateReplyRequest = z.infer<typeof createReplyRequestSchema>;

export const resolveCommentRequestSchema = z.object({
    version: commentSubjectVersionSchema,
});

export type ResolveCommentRequest = z.infer<typeof resolveCommentRequestSchema>;
